import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type WizardYesNoChoiceProps = {
  value: boolean | null;
  onChange: (value: boolean) => void;
  yesLabel: string;
  noLabel: string;
  /** Accessible name for the radio group (e.g. the step question). */
  groupLabel: string;
};

function ChoiceOption({
  selected,
  label,
  onSelect,
}: {
  selected: boolean;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors",
        selected
          ? "border-primary/40 bg-primary/5 text-primary"
          : "border-border/60 bg-card text-foreground hover:bg-muted/50",
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-primary bg-primary/10 text-primary" : "border-border/60 bg-muted/30 text-transparent",
        )}
        aria-hidden
      >
        {selected ? <Check className="h-3 w-3" strokeWidth={2.5} /> : null}
      </span>
      <span className="min-w-0 flex-1 leading-snug">{label}</span>
    </button>
  );
}

/** Vertical yes/no gate options — light outline cards; selected state uses primary tint (not solid fill). */
export default function WizardYesNoChoice({
  value,
  onChange,
  yesLabel,
  noLabel,
  groupLabel,
}: WizardYesNoChoiceProps) {
  return (
    <div className="flex flex-col gap-2" role="radiogroup" aria-label={groupLabel}>
      <ChoiceOption selected={value === true} label={yesLabel} onSelect={() => onChange(true)} />
      <ChoiceOption selected={value === false} label={noLabel} onSelect={() => onChange(false)} />
    </div>
  );
}
